import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Share2, Plane, Gift, Mail, ArrowRight } from 'lucide-react';

const InvolvementCard = ({ icon: Icon, title, description, linkText, linkTo, isEmail }) => (
  <motion.div 
    whileHover={{ y: -10 }}
    className="relative group bg-white/95 backdrop-blur-md p-8 rounded-[2.5rem] shadow-2xl flex flex-col h-full border border-white/20"
  >
    {/* Icon Container */}
    <div className="bg-gh-orange w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-gh-orange/30 group-hover:rotate-6 transition-transform">
      <Icon className="text-white" size={28} />
    </div>
    
    <h3 className="text-2xl font-black text-gh-blue mb-4 tracking-tight leading-none uppercase">
      {title}
    </h3>
    
    <p className="text-slate-600 mb-8 flex-grow leading-relaxed font-medium">
      {description}
    </p>
    
    {/* Action Link */}
    {isEmail ? (
      <a 
        href={`mailto:${linkTo}`}
        className="flex items-center gap-2 text-gh-orange font-black hover:gap-4 transition-all uppercase text-xs tracking-[0.2em]"
      >
        {linkText} <Mail size={16} />
      </a>
    ) : (
      <a 
        href={linkTo}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-gh-orange font-black hover:gap-4 transition-all uppercase text-xs tracking-[0.2em]"
      >
        {linkText} <ArrowRight size={16} />
      </a>
    )}
  </motion.div>
);

export default function GetInvolved() {
  const ways = [
    {
      icon: Heart,
      title: "One-Time Gift",
      description: "Your immediate donation helps us cover emergency medical needs, home repairs, and urgent support for our children.",
      linkText: "Give Once",
      linkTo: "/donate",
      isEmail: false
    },
    {
      icon: Share2,
      title: "Share About Us",
      description: "Help spread our mission by following and sharing our stories with your friends and family on social media.",
      linkText: "Spread the Word",
      linkTo: "https://instagram.com/givinghands", 
      isEmail: false
    },
    {
      icon: Plane,
      title: "Plan a Vision Trip",
      description: "Be one of the first to see the work we are doing on the ground in Uganda. Experience Mityana firsthand.",
      linkText: "Email Ivan",
      linkTo: "ashimweivan27@gmail.com",
      isEmail: true
    },
    {
      icon: Gift,
      title: "Holiday Fund",
      description: "Make a child's day special by contributing to our holiday celebrations and birthday gift fund throughout the year.",
      linkText: "Donate a Gift",
      linkTo: "/donate",
      isEmail: false
    }
  ];

  return (
    <section className="relative py-32 px-6 overflow-hidden min-h-[800px] flex items-center">
      {/* BACKGROUND IMAGE LAYER - CHANGED TO JPG */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/img1.jpg" 
          alt="Giving Hands Community Background" 
          className="w-full h-full object-cover"
          /* Ensure no-cache to force refresh */
        />
        {/* Dark Cinematic Overlay */}
        <div className="absolute inset-0 bg-gh-blue/85 mix-blend-multiply"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-gh-blue via-transparent to-gh-blue/20"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full">
        <div className="text-center mb-20">
          <motion.span 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-gh-orange font-black tracking-[0.4em] uppercase text-xs border-b-2 border-gh-orange pb-2 mb-6"
          >
            Take Action Today
          </motion.span>
          <h2 className="text-5xl md:text-7xl font-black text-white italic leading-tight">
            Other Ways to <br className="hidden md:block" />
            <span className="text-gh-orange text-not-italic">Get Involved</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ways.map((way, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <InvolvementCard {...way} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}